/**
 * Creative review — the portal page where any client (not only onboarding
 * ones) approves or requests changes on ads waiting for them.
 *
 * Standalone: it reuses the existing creative-review endpoints
 *   GET  app1/creative-review/<slug>/                       (one client)
 *   GET  parent-companies/<slug>/creative-review/           (parent-scope members)
 *   POST app1/creative-review/<slug>/asset/<id>/action/     (approve / request_changes / comment)
 * Ads that came from the Lead Gurus Hub report the decision back to the Hub
 * server-side; nothing here knows or cares where an ad came from.
 *
 * Route: /creative-review (optional ?client=<slug> to pick a brand).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import defaultClient from '@src/api/clients/default';
import { toast } from '@src/components/core/toaster';
import PinnableMedia, { fmtTime, pinsFromComments } from '@src/components/creative/PinnableMedia';
import { useAuth } from '@src/context';
import { CreativeOwnershipNote } from '@src/components/onboarding/HowWeWork';

const OPEN_BATCH_STATUSES = new Set(['pending_review', 'revision_uploaded', 'changes_requested']);

const STATUS_CHIP = {
  pending: { label: 'Needs review', color: 'warning' },
  approved: { label: 'Approved', color: 'success' },
  changes_requested: { label: 'Changes requested', color: 'error' },
};

const AUDIENCE_LABEL = {
  advantage_plus: 'Advantage+ audience',
  broad_55_plus: '55+ audience',
};

function resolveScope(memberships, requestedSlug) {
  const parent = memberships.find((m) => !m?.client_id && (m?.parent_company_slug || m?.parent_company_id));
  if (parent?.parent_company_slug) {
    return { kind: 'parent', slug: parent.parent_company_slug, clientSlug: requestedSlug || null };
  }
  const clients = memberships
    .filter((m) => m?.client_id)
    .map((m) => ({ slug: m.client_slug || String(m.client_id), name: m.client_name || m.client_slug || `Client ${m.client_id}` }));
  const chosen = clients.find((c) => c.slug === requestedSlug) || clients[0];
  return { kind: 'client', slug: chosen?.slug || requestedSlug || null, clients };
}

function AssetCard({ asset, clientSlug, onDone }) {
  const [mode, setMode] = useState(null); // null | 'changes' | 'comment'
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [pin, setPin] = useState(null);
  const [seek, setSeek] = useState(null);
  const chip = STATUS_CHIP[asset.asset_status] || STATUS_CHIP.pending;
  const comments = asset.comments || [];
  const pins = pinsFromComments(comments);
  const pinNumber = Object.fromEntries(pins.map((p) => [p.index, p]));

  const act = async (action, body = '') => {
    setBusy(true);
    try {
      await defaultClient.request({
        url: `app1/creative-review/${clientSlug}/asset/${asset.id}/action/`,
        method: 'POST',
        payload: { action, text: body, ...(pin && action !== 'approve' ? { pin } : {}) },
      });
      toast.success(action === 'approve' ? 'Approved' : action === 'request_changes' ? 'Feedback sent' : 'Comment added');
      setMode(null);
      setText('');
      setPin(null);
      setPlacing(false);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ bgcolor: 'background.level1', height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <PinnableMedia
          asset={asset} pins={pins} draft={pin} placing={placing} seek={seek} height={280}
          onPlace={(p) => { setPin(p); setPlacing(false); }}
          onPinClick={(p) => p.t != null && setSeek({ t: p.t })}
        />
      </Box>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" label={chip.label} color={chip.color} />
          {AUDIENCE_LABEL[asset.audience_target] && <Chip size="small" variant="outlined" label={AUDIENCE_LABEL[asset.audience_target]} />}
          {asset.placement && <Chip size="small" variant="outlined" label={asset.placement} />}
        </Stack>
        {asset.headline && <Typography variant="subtitle1" fontWeight={700}>{asset.headline}</Typography>}
        {asset.primary_text && <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{asset.primary_text}</Typography>}
        {asset.description && <Typography variant="body2" color="text.secondary">{asset.description}</Typography>}

        {comments.length > 0 && (
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1, mt: 1 }}>
            {comments.map((c, i) => ({ c, i })).slice(-3).map(({ c, i }) => (
              <Typography key={`${c.at}-${i}`} variant="caption" display="block" color="text.secondary">
                {pinNumber[i] && <Box component="span" sx={{ color: '#ff680d', fontWeight: 700, mr: 0.5 }}>#{pinNumber[i].n}</Box>}
                {pinNumber[i]?.t != null && (
                  <Box component="span" onClick={() => setSeek({ t: pinNumber[i].t })} sx={{ color: '#2563eb', cursor: 'pointer', mr: 0.5 }}>
                    {fmtTime(pinNumber[i].t)}
                  </Box>
                )}
                <strong>{c.role === 'guru' ? 'Lead Gurus' : c.author || 'You'}:</strong> {c.text}
              </Typography>
            ))}
          </Box>
        )}

        <Box sx={{ flex: 1 }} />
        {mode ? (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" variant={placing ? 'contained' : 'outlined'} onClick={() => setPlacing((v) => !v)}>
                {placing ? 'Cancel' : pin ? 'Move the pin' : 'Point to a spot'}
              </Button>
              {pin && (
                <Chip size="small" color="primary" onDelete={() => setPin(null)}
                  label={pin.t != null ? `Pinned at ${fmtTime(pin.t)}` : 'Pinned'} />
              )}
            </Stack>
            <TextField
              size="small"
              multiline
              minRows={2}
              autoFocus
              placeholder={mode === 'changes' ? 'What should we change?' : 'Add a comment'}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                disabled={busy || !text.trim()}
                onClick={() => act(mode === 'changes' ? 'request_changes' : 'comment', text.trim())}
              >
                Send
              </Button>
              <Button size="small" disabled={busy} onClick={() => { setMode(null); setText(''); setPin(null); setPlacing(false); }}>Cancel</Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1}>
            {asset.asset_status === 'pending' ? (
              <>
                <Button size="small" variant="contained" color="success" disabled={busy} onClick={() => act('approve')}>
                  Approve
                </Button>
                <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={() => setMode('changes')}>
                  Request changes
                </Button>
              </>
            ) : (
              <Button size="small" variant="text" disabled={busy} onClick={() => setMode('comment')}>
                Comment
              </Button>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function BatchSection({ batch, clientSlug, brandName, onDone }) {
  const pending = (batch.assets || []).filter((a) => a.asset_status === 'pending').length;
  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h6">{brandName ? `${brandName} — ` : ''}{batch.vertical_name || 'General'}</Typography>
        <Typography variant="body2" color="text.secondary">
          {(batch.assets || []).length} ads{pending ? ` · ${pending} waiting for you` : ''}
        </Typography>
      </Stack>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
        {(batch.assets || []).map((asset) => (
          <AssetCard key={asset.id} asset={asset} clientSlug={clientSlug} onDone={onDone} />
        ))}
      </Box>
    </Box>
  );
}

export default function CreativeReviewPage() {
  const { state } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const memberships = state?.user?.client_memberships || [];
  const scope = useMemo(
    () => resolveScope(memberships, searchParams.get('client')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(memberships), searchParams.get('client')],
  );
  const [groups, setGroups] = useState([]); // [{client_slug, client_name, batches}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('open');

  const load = useCallback(async () => {
    if (!scope.slug) {
      setLoading(false);
      return;
    }
    setError('');
    try {
      if (scope.kind === 'parent') {
        const res = await defaultClient.request({ url: `parent-companies/${scope.slug}/creative-review/` });
        setGroups(res?.data?.results || []);
      } else {
        const res = await defaultClient.request({ url: `app1/creative-review/${scope.slug}/` });
        setGroups([{ client_slug: scope.slug, client_name: '', batches: res?.data?.results || [] }]);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load your creative.');
    } finally {
      setLoading(false);
    }
  }, [scope.kind, scope.slug]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const visibleGroups = groups
    .filter((g) => !scope.clientSlug || g.client_slug === scope.clientSlug)
    .map((g) => ({
      ...g,
      batches: (g.batches || []).filter((b) =>
        filter === 'all' ? true : OPEN_BATCH_STATUSES.has(b.status) || (b.assets || []).some((a) => a.asset_status === 'pending')
      ),
    }))
    .filter((g) => g.batches.length);

  const clientOptions = scope.kind === 'parent'
    ? groups.map((g) => ({ slug: g.client_slug, name: g.client_name }))
    : scope.clients || [];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4">Creative review</Typography>
          <Typography variant="body2" color="text.secondary">
            Approve each ad, or tell us what to change. Approved ads go into your campaigns.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {clientOptions.length > 1 && (
            <TextField
              select
              size="small"
              label="Brand"
              value={scope.kind === 'parent' ? scope.clientSlug || '' : scope.slug || ''}
              onChange={(e) => {
                const next = new URLSearchParams(searchParams);
                if (e.target.value) next.set('client', e.target.value); else next.delete('client');
                setSearchParams(next);
              }}
              sx={{ minWidth: 200 }}
            >
              {scope.kind === 'parent' && <MenuItem value="">All brands</MenuItem>}
              {clientOptions.map((c) => <MenuItem key={c.slug} value={c.slug}>{c.name}</MenuItem>)}
            </TextField>
          )}
          <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}>
            <ToggleButton value="open">Needs review</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      <Box sx={{ mb: 3 }}><CreativeOwnershipNote /></Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : visibleGroups.length === 0 ? (
        <Alert severity="info">
          {filter === 'open' ? 'Nothing is waiting for your review right now.' : 'No creative has been shared with you yet.'}
        </Alert>
      ) : (
        visibleGroups.map((g) => g.batches.map((batch) => (
          <BatchSection
            key={`${g.client_slug}-${batch.id}`}
            batch={batch}
            clientSlug={g.client_slug}
            brandName={scope.kind === 'parent' ? g.client_name : ''}
            onDone={load}
          />
        )))
      )}
    </Box>
  );
}
